import { SkillsForm } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Stack, Text, Box, Alert } from 'braid-design-system';

import type { Skill } from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { logError } from 'src/modules/error-logger/browserLog';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import { useSkills } from './hooks/useSkills';
import { useTracking } from './hooks/useTracking';

type Maybe<T> = T | null;

interface Props {
  skills: Skill[];
}

export function SkillsEditor({ skills }: Props) {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { trackUpdate } = useTracking({ skills });

  const { updateSkills, error } = useSkills();

  const { onEditorClose, onFormDirty } = useEditorDrawer();

  async function onSubmit({ values }: { values: Skill[] }) {
    const skillsHaveNotBeenUpdated =
      skills.length === values.length &&
      [...skills].reverse().every((skill, index) => skill === values[index]);

    if (!skillsHaveNotBeenUpdated) {
      try {
        await updateSkills(values);
        trackUpdate(values);
      } catch (err: any) {
        logError({ ...err, type: 'skill-update' }, err);
      }
    }

    onEditorClose();
  }

  return (
    <Stack space="large">
      <SkillsForm
        locale={config.LOCALE}
        onSubmit={onSubmit}
        onCancel={() => onEditorClose()}
        // TODO: edit-ui and utils have a slight mismatch of ontologyId as optional
        initialSkills={
          skills as unknown as Array<{
            keyword: {
              text: string;
              ontologyId: Maybe<string>;
            };
          }>
        }
        onFormDirtyChange={onFormDirty}
        scrollToTopOnMobile={false}
      />
      {error && (
        <Box data-testid="skills-error">
          <Alert tone="critical">
            <Text>{t('Your skills could not be saved')}</Text>
          </Alert>
        </Box>
      )}
    </Stack>
  );
}
